import React, { ReactNode } from 'react';
import Button from './Button';
import Modal from './Modal';
import {
  CalendarIcon,
  HomeIcon,
  OpenBoxIcon,
  RouteIcon,
  VanIcon,
  WarehouseIcon,
} from './icons/JourneyIcons';

interface HowItWorksModalProps {
  show: boolean;
  close: () => void;
  storageRequired: boolean;
}

interface JourneyStep {
  title: string;
  description: string;
  icon: ReactNode;
}

const STORAGE_STEPS: JourneyStep[] = [
  {
    title: 'Book your storage',
    description:
      'Pick a date for us to collect your belongings, and choose your boxes and packing materials online.',
    icon: <CalendarIcon />,
  },
  {
    title: 'We collect your boxes',
    description:
      'On your chosen date, our drivers collect your packed boxes from your address.',
    icon: <VanIcon />,
  },
  {
    title: 'Stored securely',
    description:
      'Your boxes are kept in our secure, monitored warehouses with 24-hour security.',
    icon: <WarehouseIcon />,
  },
  {
    title: 'Ready when you need them',
    description:
      'Check your items in our online portal and book redelivery whenever you want them back.',
    icon: <OpenBoxIcon />,
  },
];

const SHIPPING_STEPS: JourneyStep[] = [
  {
    title: 'Book your shipment',
    description:
      'Choose your boxes and packing materials online, and give us the collection and delivery addresses.',
    icon: <CalendarIcon />,
  },
  {
    title: 'We collect your boxes',
    description:
      'Our drivers collect your packed boxes from your address on your chosen date.',
    icon: <VanIcon />,
  },
  {
    title: 'Safely on its way',
    description:
      'Your boxes travel securely to the destination address you gave us.',
    icon: <RouteIcon />,
  },
  {
    title: 'Delivered to the door',
    description:
      'We deliver straight to your chosen address, and you can track the whole journey in our portal.',
    icon: <HomeIcon />,
  },
];

/**
 * The journey explainer.
 *
 * Was four cards each painted a different hard-coded hex (`#f3f4f6`,
 * `#e0e7ff`, `#ddd6fe`, `#fef3c7` — Tailwind's default greys, indigos,
 * violets and ambers, none of them brand colours), which put four competing
 * accents on one panel against DESIGN_LANGUAGE.md §3. One brand tone carries
 * all four now, and the numbering does the sequencing the colours were
 * failing to do.
 */
export function HowItWorksModal({
  show,
  close,
  storageRequired,
}: HowItWorksModalProps): JSX.Element | null {
  if (!show) return null;

  const steps = storageRequired ? STORAGE_STEPS : SHIPPING_STEPS;

  return (
    <Modal
      title={`How ${storageRequired ? 'storage' : 'shipping'} works`}
      close={close}
      footer={
        <Button variant="primary" onClick={close}>
          Got it
        </Button>
      }
    >
      {/* A sequence, so an ordered list — it was a run of sibling divs. */}
      <ol className="journey">
        {steps.map((step, index) => (
          <li className="journey__step" key={step.title}>
            <span className="journey__marker">
              <span className="journey__icon">{step.icon}</span>
              <span className="journey__number" aria-hidden="true">
                {index + 1}
              </span>
            </span>
            <div className="journey__text">
              <h2 className="journey__title">{step.title}</h2>
              <p className="journey__description">{step.description}</p>
            </div>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
